// Elaboration: the checker's decisions, keyed by AST node, for backends to lower.
//
// The type checker already computes a type for every expression and resolves
// every method call, operator, and `for`-loop iterable — then discards it all
// into span-keyed hover data. An `Elaboration` captures those same decisions
// keyed by *node identity*, so a backend (the interpreter, the Go compiler) can
// read them instead of re-deriving a weaker copy of the type system from the bare
// parse tree. That re-derivation is where the informal shortcuts crept in — e.g.
// deciding iterability by "has a method named `next`" rather than "implements
// `Iterator`". With an Elaboration, there is one source of truth.
//
// This module only *defines* the IR (a leaf: it imports `ast` and `types`, and
// nothing imports it back). `checker.ts` is the producer; the backends are the
// consumers. It sits beside `lockplan.ts` — the other backend-agnostic middle-end
// IR — and is populated on the same pass the checker already makes.

import * as ast from "../frontend/ast.js";
import { Type } from "../frontend/types.js";

/** What a `call` node resolves to. `method` is the interesting one: `dynamic`
 *  (the receiver is a trait type, so it dispatches at runtime) vs. static (a
 *  concrete struct / value type / enum / container receiver, a direct call). The
 *  `receiver` type lets a backend tell a container method from a struct method. */
export type CallResolution =
  | { kind: "fn"; name: string } // a free function
  | { kind: "constructor"; name: string } // a struct or enum-variant constructor
  | { kind: "builtin"; name: string } // print / str / rand / ...
  | { kind: "fn-value"; name: string } // calling a function-typed binding or field
  | { kind: "method"; method: string; receiver: Type; dynamic: boolean }
  | { kind: "unknown" };

/** How a binary operator is realized: a native scalar/string op (or the default
 *  structural `==`), or an overload dispatched through a trait method — `+` ->
 *  `Add.add`, `==` -> `Eq.eq`, `<` -> `Ord.cmp`, etc. */
export type BinaryResolution =
  | { kind: "native" }
  | { kind: "overload"; trait: string; method: string; receiver: Type };

/** How a `for x in c` loop draws its elements, and the element type it binds `x`
 *  to. `iterable` calls `iter()` for a fresh iterator; `iterator` drives `next()`
 *  directly; `container` uses a built-in Set/Dict/Table's native iteration. */
export type IterResolution =
  | { kind: "container"; container: string; elem: Type }
  | { kind: "iterable"; typeName: string; elem: Type }
  | { kind: "iterator"; typeName: string; elem: Type }
  | { kind: "unknown"; elem: Type };

/** The checker's per-node decisions. Every map is keyed by AST node identity, so
 *  a consumer looks a decision up with the very node it is lowering. Nodes absent
 *  from a map were never reached by the checker (e.g. dead code the walk skipped),
 *  and a consumer should fall back rather than assume. */
export interface Elaboration {
  /** The inferred type of every checked expression. */
  types: Map<ast.Expr, Type>;
  /** What each call resolves to (function, constructor, builtin, method, …). */
  calls: Map<ast.CallExpr, CallResolution>;
  /** How each binary operator is realized (native vs. a trait-method overload). */
  binaries: Map<ast.BinaryExpr, BinaryResolution>;
  /** Each `for` loop's iteration plan and element type. */
  loops: Map<ast.ForStmt, IterResolution>;
  /** For each generic call / constructor, the type arguments the checker inferred
   *  (or was given explicitly), in declaration order of the callee's type
   *  parameters — so a backend reads the instantiation instead of re-inferring it.
   *  Absent for a non-generic call. */
  typeArgs: Map<ast.CallExpr, Type[]>;
}

export function emptyElaboration(): Elaboration {
  return { types: new Map(), calls: new Map(), binaries: new Map(), loops: new Map(), typeArgs: new Map() };
}
